<script setup lang="ts">
/**
 * AppHeaderMenuChoisir — contenu du menu « Choisir Donoor » de l'en-tête sur
 * ordinateur, refait sur « Choisir Shopify » (Karim 2026-11-04) : trois
 * grandes cartes à image (se lancer, changer d'outil, les associations qui
 * collectent déjà avec Donoor), puis la colonne « Inclus pour toutes les
 * associations » : Tap to Pay et l'assistant IA du chat, notre « Sidekick »,
 * avec la mascotte.
 *
 * Affiché par AppHeaderMegaMenu, qui gère l'ouverture, la hauteur et le
 * passage d'un menu à l'autre. Cartes et liens : `choisirDonoorCartes` /
 * `choisirDonoorInclus` (utils/navLinks).
 */
import { LOGOS_ASSOCIATIONS } from "~/data/logosAssociations";
import type { ComponentPublicInstance } from "vue";
import { choisirDonoorCartes, choisirDonoorInclus, type CarteChoisirDonoor } from "~/utils/navLinks";

const { t } = useI18n();

const emit = defineEmits<{
  "nav-click": [lien: { to: string }];
  fermer: [];
}>();

/** Photo des cartes ; « passer » a la vidéo, « confiance » sa mosaïque de logos. */
// Photo Unsplash (licence Unsplash, usage commercial libre) choisie par Karim
// le 2026-11-04 : Look Studio. Recadrée en 880 × 840, la forme des cartes.
const IMAGES: Partial<Record<CarteChoisirDonoor["cle"], string>> = {
  lancer: "/images/menu/choisir-lancer-telephone.webp",
};

// Carte « Passez à Donoor » : la vidéo en boucle du bloc « Vos dons arrivent
// de partout » de l'accueil (DonsPartout, Karim 2026-11-04), fond transparent,
// sur le même fond carbone. Version allégée pour la carte (660 × 550, deux fois
// sa taille à l'écran) : 5 Mo pour Safari, 3 Mo ailleurs, contre 20 et 7 Mo
// pour celle de l'accueil. Même règle des deux fichiers : le .mov HEVC
// transparent EN PREMIER avec le type « video/quicktime » (Safari), sinon
// Chrome prendrait le HEVC sans la transparence ; il lit le WebM VP9.
// Le menu n'est créé qu'à l'ouverture : la vidéo n'est téléchargée et lue
// que menu ouvert, depuis le début de la boucle. Mouvement réduit demandé :
// l'image fixe de l'accueil à la place.
const CARTE_VIDEO: CarteChoisirDonoor["cle"] = "passer";
const mouvementReduit = import.meta.client && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
// Référence par fonction : une `ref` nommée dans le v-for des cartes
// deviendrait un tableau.
let video: HTMLVideoElement | null = null;
function retenirVideo(element: Element | ComponentPublicInstance | null) {
  video = element instanceof HTMLVideoElement ? element : null;
}

onMounted(() => {
  if (!video) return;
  // `autoplay` suffit d'ordinaire ; on relance au cas où le navigateur aurait
  // posé `muted` après coup (lecture automatique refusée sinon).
  video.muted = true;
  void video.play().catch(() => {});
});

// Mosaïque inclinée de la carte « Approuvé par les associations », comme le
// mur de marques de Shopify : 20 tuiles, les 15 logos puis les 5 premiers.
const tuiles = [...LOGOS_ASSOCIATIONS, ...LOGOS_ASSOCIATIONS.slice(0, 5)];

function texte(carte: CarteChoisirDonoor, champ: "Titre" | "Texte" | "Alt") {
  return t(`navigation.appHeader.choisir.${carte.cle}${champ}`);
}

const questionPourChat = useQuestionPourChat();

function cliquer(carte: CarteChoisirDonoor) {
  if (carte.to) {
    emit("nav-click", { to: carte.to });
    return;
  }
  // Assistant : ouvre le chat du site, champ vide (cf. PublicChatWidget).
  questionPourChat.value = "";
  emit("fermer");
}

// La mascotte de la carte « Assistant » sautille tant que la souris est dessus.
const mascotteSaute = ref(false);
</script>

<template>
  <div class="choisir-panneau flex">
    <!-- Les trois cartes, à gauche. -->
    <div class="min-w-0 flex-1 px-8 pt-4 pb-9">
      <div class="mb-7 h-px bg-gray-200" />
      <div class="grid grid-cols-3 gap-x-7">
        <NuxtLink
          v-for="(carte, rang) in choisirDonoorCartes"
          :key="carte.cle"
          :to="carte.to"
          class="choisir-carte choisir-entree"
          :style="{ '--rang': rang }"
          @click.prevent="cliquer(carte)"
        >
          <div
            class="relative aspect-[22/21] overflow-hidden rounded-2xl"
            :class="
              IMAGES[carte.cle] ? 'bg-gray-100' : carte.cle === CARTE_VIDEO ? 'choisir-carbone' : 'bg-(--color-main)'
            "
          >
            <NuxtImg
              v-if="IMAGES[carte.cle]"
              :src="IMAGES[carte.cle]"
              :alt="texte(carte, 'Alt')"
              width="880"
              height="840"
              sizes="320px"
              class="choisir-image h-full w-full object-cover"
            />
            <div
              v-else-if="carte.cle === CARTE_VIDEO"
              class="choisir-image absolute inset-0"
              role="img"
              :aria-label="texte(carte, 'Alt')"
            >
              <img
                v-if="mouvementReduit"
                src="/videos/accueil/dons-partout.webp"
                alt=""
                width="1200"
                height="1000"
                decoding="async"
                class="h-full w-full object-cover"
              />
              <video
                v-else
                :ref="retenirVideo"
                class="h-full w-full object-cover"
                autoplay
                muted
                loop
                playsinline
                preload="auto"
                disablepictureinpicture
                aria-hidden="true"
                width="660"
                height="550"
              >
                <source src="/videos/menu/choisir-passer.mov" type='video/quicktime; codecs="hvc1"' />
                <source src="/videos/menu/choisir-passer.webm" type="video/webm" />
              </video>
            </div>
            <div v-else class="choisir-image absolute inset-0" role="img" :aria-label="texte(carte, 'Alt')">
              <div class="choisir-mosaique">
                <div v-for="(logo, rangLogo) in tuiles" :key="rangLogo" class="choisir-tuile">
                  <NuxtImg :src="logo.logo" alt="" :width="logo.width" :height="logo.height" sizes="80px" />
                </div>
              </div>
            </div>
          </div>
          <p class="choisir-carte-titre mt-4">{{ texte(carte, "Titre") }}</p>
          <p class="choisir-carte-texte mt-1">{{ texte(carte, "Texte") }}</p>
        </NuxtLink>
      </div>
    </div>

    <!-- Panneau de droite, comme « Disponible dans toutes les boutiques »
         chez Shopify : fond à part, collé au bord droit et au bas du menu,
         titre aligné sur le haut des images, visuels petits et sombres.
         xl+ : en dessous, les cartes seraient trop étroites (Tap to Pay reste
         dans « Plateforme »). -->
    <div
      class="choisir-entree hidden w-[316px] shrink-0 flex-col gap-6 rounded-br-[32px] bg-gray-50 px-8 pt-[45px] pb-9 xl:flex"
      :style="{ '--rang': 3 }"
    >
      <p class="choisir-titre">{{ t("navigation.appHeader.choisir.inclusTitre") }}</p>

      <template v-for="carte in choisirDonoorInclus" :key="carte.cle">
        <NuxtLink v-if="carte.to" :to="carte.to" class="choisir-carte" @click.prevent="cliquer(carte)">
          <!-- Pastille « Tap to Pay », comme la pastille « Shop Pay ». -->
          <div class="choisir-sombre grid place-items-center">
            <div class="choisir-lueur choisir-lueur--haut" aria-hidden="true" />
            <div class="choisir-lueur choisir-lueur--bas" aria-hidden="true" />
            <span class="choisir-image choisir-pastille">
              <UIcon name="i-custom-menu-tap-to-pay" class="size-[22px] text-(--color-main)" />
              Tap to Pay
            </span>
          </div>
          <p class="choisir-carte-titre mt-3">{{ texte(carte, "Titre") }}</p>
          <p class="choisir-carte-texte mt-0.5">{{ texte(carte, "Texte") }}</p>
        </NuxtLink>

        <button
          v-else
          type="button"
          class="choisir-carte w-full text-left"
          @click="cliquer(carte)"
          @mouseenter="mascotteSaute = true"
          @mouseleave="mascotteSaute = false"
        >
          <div class="choisir-sombre grid place-items-center">
            <div class="choisir-lueur choisir-lueur--haut" aria-hidden="true" />
            <div class="choisir-lueur choisir-lueur--bas" aria-hidden="true" />
            <div class="choisir-image size-[64px]" aria-hidden="true">
              <DonoorMascotte :saute="mascotteSaute" />
            </div>
          </div>
          <p class="choisir-carte-titre mt-3">{{ texte(carte, "Titre") }}</p>
          <p class="choisir-carte-texte mt-0.5">{{ texte(carte, "Texte") }}</p>
        </button>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* Mêmes mesures que le menu « Plateforme » (AppHeaderMegaMenu) : titre de
   section 13 px en majuscules espacées, bleu de marque ; titres de carte
   16 px en gras moyen ; textes 15 px gris. */
.choisir-titre {
  font-size: 13px;
  font-weight: 600;
  line-height: 1rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-main);
}

.choisir-carte {
  display: block;
  border-radius: 1rem;
  color: #0f0f0f;
}

.choisir-carte-titre {
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5rem;
  transition: color 200ms ease;
}

.choisir-carte-texte {
  font-size: 15px;
  line-height: 1.4;
  color: #6b7280;
  transition: color 200ms ease;
}

/* Survol comme le menu « Plateforme » : l'image de la carte survolée grandit
   un peu, les textes des AUTRES cartes s'éteignent en gris. Le clavier
   (focus-visible) a le même effet. */
.choisir-image {
  transition: transform 500ms cubic-bezier(0.22, 1, 0.36, 1);
}
.choisir-carte:is(:hover, :focus-visible) .choisir-image {
  transform: scale(1.04);
}
.choisir-panneau:has(.choisir-carte:is(:hover, :focus-visible))
  .choisir-carte:not(:hover, :focus-visible)
  .choisir-carte-titre {
  color: #a3a3a3;
}
.choisir-panneau:has(.choisir-carte:is(:hover, :focus-visible))
  .choisir-carte:not(:hover, :focus-visible)
  .choisir-carte-texte {
  color: #d4d4d4;
}

/* Fond de la carte vidéo : le carbone du bloc « Vos dons arrivent de
   partout » de l'accueil (DonsPartout), éclairé en haut à gauche. La vidéo,
   transparente, s'y pose ; recadrée sur les côtés (6/5 dans une carte
   presque carrée), son contenu reste entier, il est centré. */
.choisir-carbone {
  background:
    radial-gradient(90% 120% at 0% 0%, rgba(255, 255, 255, 0.09) 0%, rgba(255, 255, 255, 0) 55%),
    linear-gradient(115deg, #26262a 0%, #18181b 45%, #0d0d0e 100%);
}

/* Mur de logos incliné : tuiles blanches, logos dans leurs couleurs, sur le
   bleu de marque. Plus large que la carte pour en couvrir les coins une fois
   tourné. */
.choisir-mosaique {
  position: absolute;
  top: 50%;
  left: 50%;
  display: grid;
  width: 150%;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  transform: translate(-50%, -50%) rotate(-28deg);
}
.choisir-tuile {
  display: grid;
  aspect-ratio: 1;
  place-items: center;
  padding: 16%;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 20px -10px rgb(0 0 0 / 0.45);
}
.choisir-tuile img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* Visuels de la colonne « Inclus » : fond noir et lueur bleue de marque,
   comme la carte « Accompagnement » du menu « Plateforme ». */
.choisir-sombre {
  position: relative;
  isolation: isolate;
  height: 100px;
  overflow: hidden;
  border-radius: 1rem;
  background: #0f0f0f;
}
.choisir-lueur {
  position: absolute;
  z-index: -1;
  width: 160px;
  height: 160px;
  border-radius: 9999px;
  background: var(--color-main);
  filter: blur(44px);
  pointer-events: none;
}
.choisir-lueur--haut {
  top: -80px;
  right: -50px;
  opacity: 0.55;
}
.choisir-lueur--bas {
  bottom: -100px;
  left: -60px;
  opacity: 0.35;
}

/* Pastille blanche « Tap to Pay », au centre du visuel sombre. */
.choisir-pastille {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.125rem;
  border-radius: 9999px;
  background: #fff;
  color: #0f0f0f;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  box-shadow: 0 10px 30px -12px rgb(0 0 0 / 0.6);
}

/* Ouverture : les cartes apparaissent l'une après l'autre (40 ms d'écart),
   en montant de quelques pixels, comme les colonnes du menu « Plateforme ».
   Le contenu est créé à l'ouverture : l'animation est rejouée à chaque fois. */
.choisir-entree {
  animation: choisir-entree 380ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: calc(var(--rang, 0) * 40ms);
}

@keyframes choisir-entree {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .choisir-entree {
    animation: none;
  }
  .choisir-image,
  .choisir-carte-titre,
  .choisir-carte-texte {
    transition: none;
  }
}
</style>
